import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import Facebook from "../components/Icons/Facebook";
import Instagram from "../components/Icons/Instagram";
import Linkedin from "../components/Icons/Linkedin";
import Twitter from "../components/Icons/Twitter";
import Loading from "../components/Loading";
import MarkDown from "../components/MarkDown";
import usePageReset from "../hooks/usePageReset";
import usePersonProfile from "../hooks/usePersonProfile";
import structuredData from "../assets/structuredText";

export default function PersonProfile() {
  const { personID } = useParams();
  const [person, isLoading] = usePersonProfile(personID as string);
  usePageReset();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="keywords" content="profile person cultiv" />
        <meta
          name="description"
          content={`A personal profile page of ${
            person
              ? person.name
              : "someone who is a principal, team member or belongs to the network of Cultiv LLC "
          }`}
        />
        <meta name="robots" content="index, follow" />

        <meta property="og:url" content="https://cultiv.llc" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          itemProp="image"
          content="https://cultiv.llc/images/metaimg.jpg"
        />
        <link rel="image_src" itemProp="image" href="https://cultiv.llc/images/metaimg.jpg" />
        <meta
          property="og:description"
          content={`A personal profile page of ${
            person
              ? person.name
              : "someone who is a principal, team member or belongs to the network of Cultiv LLC "
          }`}
        />
        <meta property="og:site_name" content="Cultiv LLC" />
        <meta property="og:title" content="Cultiv | PersonProfile" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cultiv.llc" />
        <meta property="twitter:title" content="Cultiv | PersonProfile" />
        <meta
          property="twitter:description"
          content={`A personal profile page of ${
            person
              ? person.name
              : "someone who is a principal, team member or belongs to the network of Cultiv LLC "
          }`}
        />
        <meta property="twitter:image" content="https://cultiv.llc/images/metaimg.jpg" />
        <link rel="canonical" href="https://cultiv.llc" />
        <script type="application/ld+json">{structuredData}</script>
        <title>Cultiv | {`${person ? person.name : "Profile"}`}</title>
      </Helmet>
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <Loading />
        </div>
      ) : (
        <div className="w-full flex flex-col lg:gap-5 gap-3  md:px-15 lg:px-20 xl:px-30 px-6 lg:pt-10 pt-6 relative">
          <div className="flex justify-between lg:pb-2.5 pb-1 border-b-2 border-gray-400">
            <div className="flex flex-col">
              <p className="font-expandedBold text-xl md:text-3xl">{person?.name}</p>
              <div className="flex-col">
                {person.organizations?.map((org, index) => {
                  return (
                    <p key={index} className="text-sm md:text-xl text-gray-500 font-Light">
                      {org.job_title}
                    </p>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-start gap-3 items-start pt-4">
              {person?.contacts?.linkedin && (
                <a href={person.contacts.linkedin} target="_blank">
                  <Linkedin color="black" hoverColor="cultiv-yellow" />
                </a>
              )}
              {person?.contacts?.facebook && (
                <a href={person.contacts.facebook} target="_blank">
                  <Facebook color="black" hoverColor="cultiv-yellow" />
                </a>
              )}
              {person?.contacts?.twitter && (
                <a href={person.contacts.twitter} target="_blank">
                  <Twitter color="black" hoverColor="cultiv-yellow" />
                </a>
              )}
              {person?.contacts?.instagram && (
                <a href={person.contacts.instagram} target="_blank">
                  <Instagram color="black" hoverColor="cultiv-yellow" />
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-3 lg:gap-8 items-center lg:items-start">
            <img
              src={person?.profile_picture ? person?.profile_picture : "//images/2815428.png"}
              alt={person?.name}
              height={440}
              width={440}
              className="object-contain"
            />

            <div className="flex-1 lg:w-1/2">
              <MarkDown articleBody={person?.md_bio || ""} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
